import BillingMethod from 'billing/models/BillingMethod'
import { PaymentMethodEditorViewModel } from 'billing/payment-methods/PaymentMethodEditorViewModel'
import PaymentMethodSelectorViewModel, {
  OnSaveDelegate,
} from 'billing/payment-methods/PaymentMethodSelectorViewModel'
import {
  CardPaymentMethodEditorPorts,
  PaymentMethodSelectorPorts,
} from 'billing/payment-methods/ports'
import Member from 'iam/models/Member'
import Team from 'iam/models/Team'
import { CancellationToken } from 'infra/cancellation'
import ModalViewModel from 'infra/ModalViewModel'
import { action, observable, makeObservable } from 'mobx'
import { race, lastValueFrom } from 'rxjs'
import { startWith, take, map } from 'rxjs/operators'
import CardPaymentMethodEditorViewModel from './CardPaymentMethodEditorViewModel'

/**
 * A delegate that can be called to create a new
 * payment method.
 */
export type NewPaymentMethodDelegate = (
  member: Member,
  team: Team | null,
  ct: CancellationToken
) => Promise<BillingMethod | null>

/**
 * A delegate that can be called to select a
 * payment method.
 */
export type SelectPaymentMethodDelegate = (
  member: Member,
  team: Team | null,
  currentPaymentMethod: BillingMethod | null,
  onSave: OnSaveDelegate | null,
  ct: CancellationToken
) => Promise<BillingMethod | null>

/**
 * Root view model for Payment Methods.
 */
export default class PaymentMethodsRootViewModel {
  /**
   * The last opened payment method editor modal.
   */
  @observable
  newPaymentMethodEditorModal: NewPaymentMethodEditorModalViewModel | null =
    null

  /**
   * The last opened payment selector modal.
   */
  @observable
  paymentMethodSelectorModal: PaymentMethodSelectorModalViewModel | null = null

  /**
   * Constructor.
   * @param cardPaymentMethodEditorAdapter
   * @param paymentMethodSelectorAdapter
   */
  constructor(
    private cardPaymentMethodEditorAdapter: CardPaymentMethodEditorPorts,
    private paymentMethodSelectorAdapter: PaymentMethodSelectorPorts
  ) {
    makeObservable(this)
    this.selectPaymentMethod = this.selectPaymentMethod.bind(this)
    this.newPaymentMethodModal = this.newPaymentMethodModal.bind(this)
  }

  /**
   * Starts the New Payment Method flow and returns a Promise
   * with the new Billing Method (or `null` if cancelled).
   *
   * @param member
   * @param team
   * @param ct
   */
  async newPaymentMethodModal(
    member: Member,
    team: Team | null,
    ct: CancellationToken
  ): Promise<BillingMethod | null> {
    const editor = new CardPaymentMethodEditorViewModel(
      member,
      team,
      this.cardPaymentMethodEditorAdapter
    )
    const modal = this.openPaymentMethodEditor(editor, ct)
    await editor.prepare()
    const method = await lastValueFrom(modal.onPaymentMethodCreated$)
    modal.close()
    return method
  }

  /**
   * Starts a Select Payment Method flow and returns a Promise
   * with the chosen Billing Method (or `null` if cancelled).
   *
   * @param member
   * @param team
   * @param currentPaymentMethod
   * @param onSave
   * @param ct
   */
  async selectPaymentMethod(
    member: Member,
    team: Team | null,
    currentPaymentMethod: BillingMethod | null,
    onSave: OnSaveDelegate | null,
    ct: CancellationToken
  ): Promise<BillingMethod | null> {
    const selector = new PaymentMethodSelectorViewModel({
      onSave,
      // An active observable created from a cancellation token.
      active$: ct.asObservable().pipe(
        map(() => false),
        startWith(true)
      ),
      paymentMethods: () =>
        this.paymentMethodSelectorAdapter.billingMethodsFor(member, team),
      canAddMethod: () =>
        this.paymentMethodSelectorAdapter.canAddMethod(member, team),
      fetchBillingProfile: () =>
        this.paymentMethodSelectorAdapter.fetchBillingProfile(member, team),
      newPaymentMethod: (ct) => this.newPaymentMethodModal(member, team, ct),
    })
    const modal = this.openPaymentMethodSelector(selector, ct)
    await selector.prepare(currentPaymentMethod)
    const method = await lastValueFrom(modal.onPaymentMethodSelected$)
    modal.close()
    return method
  }

  /**
   * Opens a payment method editor modal.
   *
   * @param editor
   * @param ct
   * @private
   */
  @action.bound
  private openPaymentMethodEditor(
    editor: PaymentMethodEditorViewModel,
    ct: CancellationToken
  ) {
    this.newPaymentMethodEditorModal?.close()
    this.newPaymentMethodEditorModal = new NewPaymentMethodEditorModalViewModel(
      editor,
      ct
    )
    this.newPaymentMethodEditorModal.show()
    return this.newPaymentMethodEditorModal
  }

  /**
   * Opens a payment method selector modal.
   *
   * @param selector
   * @param ct
   * @private
   */
  @action.bound
  private openPaymentMethodSelector(
    selector: PaymentMethodSelectorViewModel,
    ct: CancellationToken
  ) {
    this.paymentMethodSelectorModal?.close()
    this.paymentMethodSelectorModal = new PaymentMethodSelectorModalViewModel(
      selector,
      ct
    )
    this.paymentMethodSelectorModal.show()
    return this.paymentMethodSelectorModal
  }
}

/**
 * Modal for the new payment method editor.
 */
export class NewPaymentMethodEditorModalViewModel extends ModalViewModel {
  /**
   * Emits the created payment method, or `null` if the operation is cancelled
   * or the modal is closed.
   */
  onPaymentMethodCreated$ = race(
    this.editor.onPaymentMethodCreated$,
    this.close$.pipe(map(() => null))
  ).pipe(take(1))

  constructor(
    public editor: PaymentMethodEditorViewModel,
    private ct: CancellationToken
  ) {
    super(ct)
  }
}

/**
 * Modal for the payment method selector.
 */
export class PaymentMethodSelectorModalViewModel extends ModalViewModel {
  /**
   * Emits the created payment method, or `null` if the operation is cancelled
   * or the modal is closed.
   */
  onPaymentMethodSelected$ = race(
    this.selector.onPaymentMethodSelected$,
    this.close$.pipe(map(() => null))
  ).pipe(take(1))

  constructor(
    public selector: PaymentMethodSelectorViewModel,
    private ct: CancellationToken
  ) {
    super(ct)
  }
}
